
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Clock, FileText, Send, AlertCircle, CheckCircle, QrCode } from 'lucide-react';

interface Dispute {
  id: string;
  disputeId: string;
  title: string;
  provider: string;
  dateSubmitted: string;
  dueDate: string;
  status: 'submitted' | 'acknowledged' | 'under_review' | 'resolved' | 'overdue';
  type: 'condition' | 'medication' | 'lab' | 'procedure';
  description: string;
  qrCodeUrl?: string;
  providerResponseUrl?: string;
  auditTrail: Array<{
    date: string;
    action: string;
    details: string;
  }>;
}

interface DisputeTrackingProps {
  selectedPatientId?: string;
}

export const DisputeTracking = ({ selectedPatientId = 'me' }: DisputeTrackingProps) => {
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null);

  useEffect(() => {
    loadActiveDisputes();
  }, [selectedPatientId]);

  const loadActiveDisputes = () => {
    const mockDisputes: Dispute[] = [
      {
        id: '1',
        disputeId: 'HSC-2024-001',
        title: 'Hypertension - Outdated Diagnosis',
        provider: 'Dr. Sarah Smith - Primary Care',
        dateSubmitted: '2024-06-01',
        dueDate: '2024-07-31',
        status: 'under_review',
        type: 'condition',
        description: 'Hypertension diagnosis appears outdated based on recent normal BP readings',
        qrCodeUrl: 'https://healthscore.app/dispute/HSC-2024-001',
        auditTrail: [
          { date: '2024-06-01', action: 'Dispute Submitted', details: 'HIPAA amendment request sent via fax' },
          { date: '2024-06-03', action: 'Acknowledged', details: 'Provider acknowledged receipt' },
          { date: '2024-06-15', action: 'Under Review', details: 'Clinical team reviewing case' }
        ]
      },
      {
        id: '2',
        disputeId: 'HSC-2024-002',
        title: 'Lisinopril Dosage Error',
        provider: 'Dr. Sarah Smith - Primary Care',
        dateSubmitted: '2024-05-15',
        dueDate: '2024-07-14',
        status: 'overdue',
        type: 'medication',
        description: 'Medication dosage listed as 10mg but current prescription is 5mg',
        qrCodeUrl: 'https://healthscore.app/dispute/HSC-2024-002',
        auditTrail: [
          { date: '2024-05-15', action: 'Dispute Submitted', details: 'HIPAA amendment request sent via fax' },
          { date: '2024-07-14', action: 'Follow-up Notice', details: 'Automatic follow-up sent due to no response' }
        ]
      },
      {
        id: '3',
        disputeId: 'HSC-2024-003',
        title: 'Cholesterol Results Update',
        provider: 'LabCorp',
        dateSubmitted: '2024-06-20',
        dueDate: '2024-08-19',
        status: 'acknowledged',
        type: 'lab',
        description: 'Outdated high cholesterol result needs to be updated with recent normal values',
        qrCodeUrl: 'https://healthscore.app/dispute/HSC-2024-003',
        auditTrail: [
          { date: '2024-06-20', action: 'Dispute Submitted', details: 'HIPAA amendment request sent via fax' },
          { date: '2024-06-22', action: 'Acknowledged', details: 'Lab confirmed receipt and processing' }
        ]
      }
    ];

    setDisputes(mockDisputes);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return 'bg-blue-100 text-blue-700';
      case 'acknowledged': return 'bg-green-100 text-green-700';
      case 'under_review': return 'bg-yellow-100 text-yellow-700';
      case 'resolved': return 'bg-green-100 text-green-700';
      case 'overdue': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'submitted': return <Send className="h-4 w-4" />;
      case 'acknowledged': return <CheckCircle className="h-4 w-4" />;
      case 'under_review': return <Clock className="h-4 w-4" />;
      case 'resolved': return <CheckCircle className="h-4 w-4" />;
      case 'overdue': return <AlertCircle className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getProgress = (status: string) => {
    switch (status) {
      case 'submitted': return 25;
      case 'acknowledged': return 50;
      case 'under_review': return 75;
      case 'resolved': return 100;
      case 'overdue': return 25;
      default: return 0;
    }
  };

  const getDaysRemaining = (dueDate: string) => {
    const due = new Date(dueDate);
    const now = new Date();
    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <h3 className="text-lg font-semibold mb-2">Active Disputes</h3>
        <p className="text-sm text-muted-foreground">
          Track your ongoing dispute cases. Each dispute has a unique ID and QR code for provider reference.
        </p>
      </Card>

      <div className="space-y-4">
        {disputes.map((dispute) => (
          <Card key={dispute.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                {getStatusIcon(dispute.status)}
                <h4 className="font-medium">{dispute.title}</h4>
                <Badge className={getStatusColor(dispute.status)}>
                  {dispute.status.replace('_', ' ')}
                </Badge>
              </div>
              <div className="text-right">
                <p className="text-sm font-mono text-muted-foreground">{dispute.disputeId}</p>
                <p className="text-xs text-muted-foreground">
                  {getDaysRemaining(dispute.dueDate) > 0 
                    ? `${getDaysRemaining(dispute.dueDate)} days remaining`
                    : `${Math.abs(getDaysRemaining(dispute.dueDate))} days overdue`
                  }
                </p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mb-3">{dispute.description}</p>
            <p className="text-sm mb-3"><strong>Provider:</strong> {dispute.provider}</p>

            <div className="mb-3">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-muted-foreground">Progress</span>
                <span className="text-xs text-muted-foreground">{getProgress(dispute.status)}%</span>
              </div>
              <Progress value={getProgress(dispute.status)} className="h-2" />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm">
                  <QrCode className="h-4 w-4 mr-1" />
                  View QR Code
                </Button>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedDispute(dispute)}
                    >
                      View Details
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Dispute Details - {selectedDispute?.disputeId}</DialogTitle>
                    </DialogHeader>
                    {selectedDispute && (
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium mb-2">Audit Trail</h4>
                          <div className="space-y-2">
                            {selectedDispute.auditTrail.map((entry, index) => (
                              <div key={index} className="flex items-start space-x-3 p-2 rounded border">
                                <div className="text-xs text-muted-foreground min-w-20">
                                  {new Date(entry.date).toLocaleDateString()}
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-medium">{entry.action}</p>
                                  <p className="text-xs text-muted-foreground">{entry.details}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </div>
              <div className="text-xs text-muted-foreground">
                Submitted: {new Date(dispute.dateSubmitted).toLocaleDateString()}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
